import { Controller } from "@hotwired/stimulus"
import { Modal } from "bootstrap"
import { getCsrfToken } from "shared/form_helpers"
import { sendSuccessNotification, sendFailureNotification } from "shared/toast_notifications"

// Connects to data-controller="settings--print-classes-table"
export default class extends Controller {
    static targets = [
        "table",
        "createModal",
        "createForm",
        "nameInput",
        "paperTypeIdInput",
        "editModal",
        "editForm",
        "editNameInput",
        "editIdInput"
    ]

    static values = {
        paperTypeId: String
    }

    connect() {
        this.initializeTable()
        this.loadPrintClasses()
    }

    initializeTable() {
        // No initialization needed — argus--table controller handles this
    }

    _getArgusTable() {
        if (!this.hasTableTarget) return null
        return this.application.getControllerForElementAndIdentifier(
            this.tableTarget, 'argus--table'
        )
    }

    async loadPrintClasses() {
        if (!this.paperTypeIdValue) {
            console.error('No paper type ID provided')
            return
        }

        const argusTable = this._getArgusTable()
        argusTable?.showLoading()
        try {
            // Use the list endpoint instead of describe, since describe expects print class IDs
            const response = await fetch(`/api/print_mappings/print_class/list`, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'X-CSRF-Token': getCsrfToken()
                }
            })

            if (!response.ok) {
                throw new Error('Failed to fetch print classes')
            }

            const data = await response.json()
            
            // Filter print classes by paper type ID
            const allPrintClasses = data.print_classes || []
            const printClasses = allPrintClasses.filter(pc => pc.paper_type === this.paperTypeIdValue)
            
            this.populateTable(printClasses)
        } catch (error) {
            console.error('Error loading print classes:', error)
            sendFailureNotification('Failed to load print classes')
        } finally {
            argusTable?.hideLoading()
        }
    }

    populateTable(printClasses) {
        if (this.hasTableTarget) {
            const argusTable = this.application.getControllerForElementAndIdentifier(
                this.tableTarget, 'argus--table'
            )
            if (argusTable) {
                argusTable.loadData(printClasses)
            }
        }
    }

    openCreateModal(event) {
        event.preventDefault()
        
        // Clear form
        if (this.hasNameInputTarget) {
            this.nameInputTarget.value = ''
        }
        
        // Set paper type ID
        if (this.hasPaperTypeIdInputTarget) {
            this.paperTypeIdInputTarget.value = this.paperTypeIdValue
        }

        // Show modal
        const modal = new Modal(this.createModalTarget)
        modal.show()
    }

    async handleCreateSubmit(event) {
        event.preventDefault()

        const formData = new FormData(this.createFormTarget)

        try {
            const response = await fetch('/api/print_mappings/print_class/create', {
                method: 'POST',
                headers: {
                    'X-CSRF-Token': getCsrfToken()
                },
                body: formData
            })

            if (!response.ok) {
                throw new Error('Failed to create print class')
            }

            // Close modal
            const modal = Modal.getInstance(this.createModalTarget)
            modal.hide()

            // Reload table
            await this.loadPrintClasses()

            sendSuccessNotification('Print class created successfully')
        } catch (error) {
            console.error('Error creating print class:', error)
            sendFailureNotification('Failed to create print class')
        }
    }

    openEditModal(event) {
        event.preventDefault()

        const button = event.currentTarget
        const printClassId = button.dataset.printClassId

        // Fetch print class details
        this.fetchPrintClassDetails(printClassId)
    }

    async fetchPrintClassDetails(printClassId) {
        try {
            const response = await fetch(`/api/print_mappings/print_class/describe?id=${printClassId}`, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'X-CSRF-Token': getCsrfToken()
                }
            })

            if (!response.ok) {
                throw new Error('Failed to fetch print class details')
            }

            const data = await response.json()
            const printClass = data.print_classes[0]

            // Populate edit form
            if (this.hasEditNameInputTarget) {
                this.editNameInputTarget.value = printClass.name || ''
            }
            if (this.hasEditIdInputTarget) {
                this.editIdInputTarget.value = printClass.id
            }

            // Show modal
            const modal = new Modal(this.editModalTarget)
            modal.show()
        } catch (error) {
            console.error('Error fetching print class details:', error)
            sendFailureNotification('Failed to load print class details')
        }
    }

    async handleEditSubmit(event) {
        event.preventDefault()

        const formData = new FormData(this.editFormTarget)

        try {
            const response = await fetch('/api/print_mappings/print_class/update', {
                method: 'PATCH',
                headers: {
                    'X-CSRF-Token': getCsrfToken()
                },
                body: formData
            })

            if (!response.ok) {
                throw new Error('Failed to update print class')
            }

            // Close modal
            const modal = Modal.getInstance(this.editModalTarget)
            modal.hide()

            // Reload table
            await this.loadPrintClasses()

            sendSuccessNotification('Print class updated successfully')
        } catch (error) {
            console.error('Error updating print class:', error)
            sendFailureNotification('Failed to update print class')
        }
    }

    async deletePrintClass(event) {
        event.preventDefault()

        const button = event.currentTarget
        const printClassId = button.dataset.printClassId

        // Get print class name from table row
        const row = button.closest('tr')
        const nameCell = row.querySelector('td:first-child')
        const printClassName = nameCell ? nameCell.textContent : 'this print class'

        if (!confirm(`Are you sure you want to delete ${printClassName}?`)) {
            return
        }

        const formData = new FormData()
        formData.append('id', printClassId)

        try {
            const response = await fetch('/api/print_mappings/print_class/delete', {
                method: 'DELETE',
                headers: {
                    'X-CSRF-Token': getCsrfToken()
                },
                body: formData
            })

            if (!response.ok) {
                throw new Error('Failed to delete print class')
            }

            // Reload table
            await this.loadPrintClasses()

            sendSuccessNotification('Print class deleted successfully')
        } catch (error) {
            console.error('Error deleting print class:', error)
            sendFailureNotification('Failed to delete print class')
        }
    }
}
